import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingAnimation from '../LoadingAnimation';
import RichTextEditorSimple from './RichTextEditorSimple';

interface BlogEditorProps {
  darkMode?: boolean;
}

interface BlogFormData {
  title: string;
  slug: string;
  content: string;
  author_name: string;
  short_description: string;
  publish_date: string;
}

interface UploadedImage {
  asset_id: string;
  asset_url: string;
  filename: string;
}


const BlogEditor: React.FC<BlogEditorProps> = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId?: string }>();
  const isEditMode = !!postId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [featuredImage, setFeaturedImage] = useState<UploadedImage | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    slug: '',
    content: '',
    author_name: '',
    short_description: '',
    publish_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    checkUserAuth();
    if (isEditMode) {
      loadExistingPost();
    } else {
      // Set default author name from localStorage
      const userName = localStorage.getItem('userName') || '';
      setFormData(prev => ({ ...prev, author_name: userName }));
    }
  }, [isEditMode]);

  // Helper function to get the current user's token
  const getCurrentToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('recruiterToken');
  };

  const checkUserAuth = () => {
    const adminToken = localStorage.getItem('token');
    const recruiterToken = localStorage.getItem('recruiterToken');
    
    // Determine user role
    if (adminToken) {
      setUserRole('admin');
    } else if (recruiterToken) {
      setUserRole('recruiter');
    } else {
      console.log('User not authenticated');
      setUserRole(null);
    }
  };

  const loadExistingPost = async () => {
    if (!postId) return;
    
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${apiUrl}/api/blog/admin/posts/${postId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch post for editing');
      }
      
      const post = await response.json();
      
      // Debug the content we're receiving
      console.log('DEBUG BlogEditor: Post data received:', post);
      console.log('DEBUG BlogEditor: Content field:', post.fields?.content);
      console.log('DEBUG BlogEditor: Content type:', typeof post.fields?.content);
      
      // Convert content object to JSON string for the RichTextEditor
      let contentForEditor = '';
      if (post.fields?.content) {
        if (typeof post.fields.content === 'object') {
          contentForEditor = JSON.stringify(post.fields.content);
          console.log('DEBUG BlogEditor: Converted object to JSON string, length:', contentForEditor.length);
        } else {
          contentForEditor = post.fields.content;
          console.log('DEBUG BlogEditor: Content already string, length:', contentForEditor.length);
        }
      }
      
      // Populate form with existing data
      setFormData({
        title: post.fields?.title || '',
        slug: post.fields?.slug || '',
        content: contentForEditor,
        author_name: post.fields?.author?.fields?.name || '',
        short_description: post.fields?.shortDescription || '',
        publish_date: post.fields?.publishedDate || new Date().toISOString().split('T')[0]
      });
      
      // TODO: Load existing blog images if they exist
      // For now, blog images will need to be re-uploaded when editing
      
    } catch (err) {
      console.error('Error loading post for editing:', err);
      setError('Failed to load post for editing');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from title
    if (name === 'title' && !isEditMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Please upload an image smaller than 5MB.');
      return;
    }

    setUploadingImage(true);
    setUploadProgress(0);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress updates since fetch doesn't support upload progress natively
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) return prev; // Cap at 90% until completion
          return prev + Math.random() * 10;
        });
      }, 200);

      const response = await fetch(`${apiUrl}/api/blog/upload-image`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const result = await response.json();
      
      if (result.success) {
        setFeaturedImage({
          asset_id: result.asset_id,
          asset_url: result.asset_url,
          filename: result.filename
        });
      } else {
        throw new Error('Image upload failed');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = () => {
    setFeaturedImage(null);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    // Validation
    if (!formData.title || !formData.slug || !formData.content || !formData.author_name) {
      setError('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      let response, result;
      
      if (isEditMode) {
        // Update existing post
        response = await fetch(`${apiUrl}/api/blog/${postId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            title: formData.title,
            content: formData.content,
            short_description: formData.short_description,
            author_name: formData.author_name,
            featured_image_id: featuredImage?.asset_id || null
          })
        });
      } else {
        // Always use the original endpoint but pass featured_image_id if available
        const createData = featuredImage ? {
          ...formData,
          featured_image_id: featuredImage.asset_id
        } : formData;
        
        console.log('DEBUG Frontend: Using original create endpoint');
        console.log('DEBUG Frontend: Create data:', createData);
        console.log('DEBUG Frontend: Content field:', createData.content);
        
        response = await fetch(`${apiUrl}/api/blog/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(createData)
        });
      }
      
      result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || `Failed to ${isEditMode ? 'update' : 'create'} blog post`);
      }
      
      if (result.success) {
        setSuccessMessage(`Blog post ${isEditMode ? 'updated' : 'created'} successfully!`);
        
        if (isEditMode) {
          alert('Post updated successfully!');
          // Reload the post data to reflect changes
          await loadExistingPost();
        } else {
          // Check if user is admin and has featured image
          if (userRole === 'admin' && featuredImage) {
            // Only admins can publish directly
            if (window.confirm('Post created successfully! Would you like to publish it now?')) {
              await publishPost(result.entry_id);
            } else {
              alert('Post saved as draft. You can publish it later from the admin panel.');
            }
          } else if (userRole === 'recruiter') {
            // Recruiters always save as draft for admin review
            alert('Post created successfully and saved as draft. An admin will review and publish it.');
          } else {
            // No featured image or other cases
            alert('Post created as draft! Add a featured image and publish from the admin panel for best results.');
          }
        }
        setTimeout(() => navigate('/blog-admin'), 2000);
      } else {
        throw new Error(result.message || `Failed to ${isEditMode ? 'update' : 'create'} blog post`);
      }
    } catch (err) {
      console.error('Error saving blog post:', err);
      setError(err instanceof Error ? err.message : 'Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const publishPost = async (entryId: string) => {
    try {
      const token = getCurrentToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${apiUrl}/api/blog/${entryId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccessMessage('Blog post published successfully!');
        setTimeout(() => navigate('/blog-admin'), 2000);
      } else {
        // Publishing might fail due to missing required fields in Contentful
        alert(`Post created but couldn't publish: ${result.error || result.message}`);
        setTimeout(() => navigate('/blog-admin'), 2000);
      }
    } catch (err) {
      console.error('Error publishing post:', err);
      alert('Post created but failed to publish. You can publish it later from the admin panel.');
      setTimeout(() => navigate('/blog-admin'), 2000);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-3xl font-bold">
              {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h1>
            <button
              onClick={() => navigate('/blog-admin')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                darkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ← Blog Admin
            </button>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <p>{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p>{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Title */}
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-medium mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full p-3 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
            </div>

            {/* Slug */}
            <div className="mb-6">
              <label htmlFor="slug" className="block text-sm font-medium mb-2">
                URL Slug *
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className={`w-full p-3 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
                disabled={isEditMode}
              />
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                This will be the URL: /blog/{formData.slug || 'your-url-slug'}
              </p>
            </div>

            {/* Author */}
            <div className="mb-6">
              <label htmlFor="author_name" className="block text-sm font-medium mb-2">
                Author Name *
              </label>
              <input
                type="text"
                id="author_name"
                name="author_name"
                value={formData.author_name}
                onChange={handleInputChange}
                className={`w-full p-3 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
            </div>

            {/* Short Description */}
            <div className="mb-6">
              <label htmlFor="short_description" className="block text-sm font-medium mb-2">
                Short Description / Excerpt
              </label>
              <textarea
                id="short_description"
                name="short_description"
                value={formData.short_description}
                onChange={handleInputChange}
                rows={2}
                className={`w-full p-3 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Brief summary of the blog post..."
              />
            </div>

            {/* Publish Date */}
            <div className="mb-6">
              <label htmlFor="publish_date" className="block text-sm font-medium mb-2">
                Publish Date
              </label>
              <input
                type="date"
                id="publish_date"
                name="publish_date"
                value={formData.publish_date}
                onChange={handleInputChange}
                className={`w-full p-3 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            {/* Featured Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Featured Image
              </label>
              
              {!featuredImage ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className={`w-full p-3 border rounded-lg ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  {uploadingImage && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-blue-600">Uploading image...</span>
                        <span className="text-blue-600 font-medium">{Math.round(uploadProgress)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Upload a featured image for your blog post (JPEG, PNG, GIF, WebP, max 5MB)
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className={`p-4 border rounded-lg ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {featuredImage.asset_url && (
                          <img
                            src={featuredImage.asset_url}
                            alt="Featured image preview"
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium">{featuredImage.filename}</p>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Image uploaded successfully
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="mb-6">
              <label htmlFor="content" className="block text-sm font-medium mb-2">
                Content *
              </label>
              <RichTextEditorSimple
                value={formData.content}
                onChange={handleContentChange}
                darkMode={darkMode}
                placeholder="Write your blog post content here... Use the toolbar above to format your text with headings, lists, bold, italic, and more!"
              />
            </div>


            {/* Buttons */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => navigate('/blog-admin')}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Post' : 'Create Post')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEditor;